import React, { useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import NewsletterSignUp from '../../common/Newsletter-sign';
import Footer from '../../Footer/Footer';
import ImageSlant from '../../common/image-slant';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const festive = 'https://BelvoirCastle.b-cdn.net/Christmas/festive.webp';

const Page = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background-color: ${props => props.theme.colors.offWhite};
`;

const Hero = styled.div`
  width: 100%;
  height: 420px;
  background-image: ${props => `linear-gradient(to bottom, rgba(0,0,0,0.25), rgba(0,0,0,0.65)), url(${props.image})`};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const HeroInner = styled.div`
  max-width: 1400px;
  width: 100%;
  padding: 3rem 2rem;
  color: white;
`;

const HeroTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
`;

const HeroSub = styled.p`
  font-size: 1.25rem;
  margin: 0;
  opacity: 0.95;
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 1.5rem auto 0;
  padding: 0 2rem 3rem;
`;

const Card = styled.section.attrs({ className: 'bf-surface' })`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1.5rem;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: '';
    position: absolute;
    left: 0; bottom: 0;
    width: 80px; height: 3px;
    background: ${props => props.theme.colors.accent};
  }
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FAQItem = styled.div.attrs({ className: 'bf-surface' })`
  background: ${props => props.theme.colors.offWhite};
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.06);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  }
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 1.25rem 1.5rem;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  transition: all 0.2s ease;

  &:hover {
    color: ${props => props.theme.colors.accent};
  }
`;

const FAQAnswer = styled.div`
  max-height: ${props => props.$isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease, padding 0.3s ease;
  padding: ${props => props.$isOpen ? '0 1.5rem 1.25rem' : '0 1.5rem'};
  color: #555;
  font-size: 1rem;
  line-height: 1.7;
`;

const Icon = styled.span`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.accent};
  transition: transform 0.3s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  flex-shrink: 0;
  margin-left: 1rem;
`;

const CTAButton = styled.a.attrs({ className: 'cta-button' })`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.9rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 700;
  margin-top: 1.25rem;
  transition: all 0.2s ease;
  &:hover { opacity: 0.95; transform: translateY(-2px); }
`;

const YourFestiveVisitFAQs = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';
  const [openFAQ, setOpenFAQ] = useState(null);

  const meta = {
    title: 'FAQs | Your Christmas Visit | Christmas 2026 at Belvoir Castle',
    description: 'Frequently asked questions about your Christmas visit to Belvoir Castle, including booking, tickets, Family Saver, and more.',
    canonical: 'https://www.belvoircastle.com/christmas/your-festive-visit/faqs'
  };

  const faqs = [
    {
      question: 'How do I book my Christmas visit?',
      answer: 'Start by booking your Entry Ticket or Search for Santa Entry Ticket through our booking system. Once you have your entry ticket, you can add extras like Santa\'s Grotto and Festive Afternoon Tea. The booking system will guide you through selecting dates and timeslots for each experience.'
    },
    {
      question: 'What is Family Saver and how does it work?',
      answer: 'Family Saver is our way of rewarding larger groups. The more people you add to your booking, the more you save. As you build your group in the booking system, your combined price updates automatically to show available savings. Simply add your partner, children, grandparents, and friends to see the savings stack up.'
    },
    {
      question: 'What\'s included with an Entry Ticket?',
      answer: 'Your Entry Ticket includes full day access to the Castle and grounds, plus the Mice Trail. The Mice Trail is a charming family trail throughout the Castle that\'s included at no extra cost. You can then add Santa\'s Grotto and Festive Afternoon Tea as optional extras.'
    },
    {
      question: 'What is Search for Santa and how is it different from Entry?',
      answer: 'Search for Santa is a separate ticket that includes a late afternoon magical family trail through the Castle (5:00pm – 7:30pm on selected December dates). Children enjoy hot chocolate, a biscuit and a present from Santa; parents get refreshments and a glass of bubbles. It also includes full-day access to the Castle and grounds, and the Mice Trail. This is a separate ticket, not an add-on to Entry.'
    },
    {
      question: 'Can I add Santa\'s Grotto and Festive Afternoon Tea to my booking?',
      answer: 'Yes! Both Santa\'s Grotto and Festive Afternoon Tea are add-ons that you can book after securing your Entry Ticket. Simply select your preferred timeslots when building your package. We recommend booking early as these experiences are popular and have limited availability.'
    },
    {
      question: 'What are the opening times during Christmas?',
      answer: 'The Castle is open from 14 November to 30 December, 10am to 5pm daily. Please check your booking confirmation for specific timeslots for add-on experiences like Santa\'s Grotto and Festive Afternoon Tea.'
    },
    {
      question: 'Do I need to book in advance?',
      answer: 'Yes, we strongly recommend booking in advance, especially for popular dates and experiences like Santa\'s Grotto and Festive Afternoon Tea. Pre-booking ensures you get your preferred date and timeslots, and online prices are better than ticket office prices.'
    },
    {
      question: 'What are the ticket prices?',
      answer: 'Entry Ticket prices start at £30.00 for adults, £15.00 for children, and £27.00 for concessions when booked online. Ticket office prices are slightly higher. Family Saver discounts apply automatically as you add more people to your group. Search for Santa, Santa\'s Grotto, and Festive Afternoon Tea have separate pricing - check the individual pages for current prices.'
    },
    {
      question: 'Can I change or cancel my booking?',
      answer: 'We do not offer refunds, but we can reschedule your booking to an alternative date. Please contact our booking team if you need to change your visit date. We recommend reviewing our booking terms and conditions before completing your booking.'
    },
    {
      question: 'Is the Castle accessible?',
      answer: 'The Castle has limited access due to its historic nature - only the ground floor is accessible. Please visit our accessibility page for detailed information about access, parking, and facilities. If you have specific accessibility requirements, we recommend contacting us before your visit so we can ensure the best experience.'
    },
    {
      question: 'What should I wear?',
      answer: 'We recommend wearing comfortable shoes as there\'s plenty to explore. The Castle is warm with lit fires - it\'s a family home so it is warm inside. For Festive Afternoon Tea, smart casual dress is appropriate.'
    },
    {
      question: 'Is parking available?',
      answer: 'Yes, parking is available on-site for £4 all day. Please check our directions page for details about parking locations.'
    },
    {
      question: 'Can I visit Santa\'s Grotto at the Belvoir Retail Village?',
      answer: 'Yes! Santa\'s Grotto at the Belvoir Retail Village can be visited standalone with a £15 ticket, or you can make a full day out by pairing it with a Castle ticket. If you add a Castle ticket, it will show as an additional add-on in the booking system.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <Page className={pageClassName}>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content={festive} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Christmas Visit FAQs at Belvoir Castle" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={festive} />
        <meta name="twitter:site" content="@belvoircastle" />
        <meta name="twitter:url" content={meta.canonical} />
        <meta name="twitter:image:alt" content="Christmas Visit FAQs at Belvoir Castle" />
      </Helmet>

      <Hero image={festive}>
        <HeroInner>
          <HeroTitle>Frequently Asked Questions</HeroTitle>
          <HeroSub>Everything you need to know about your Christmas visit</HeroSub>
        </HeroInner>
      </Hero>

      <Content>
        <Card>
          <SectionTitle>Your Christmas Visit FAQs</SectionTitle>
          <FAQList>
            {faqs.map((faq, index) => (
              <FAQItem key={index}>
                <FAQQuestion onClick={() => toggleFAQ(index)}>
                  <span>{faq.question}</span>
                  <Icon $isOpen={openFAQ === index}>▼</Icon>
                </FAQQuestion>
                <FAQAnswer $isOpen={openFAQ === index}>
                  {faq.answer}
                </FAQAnswer>
              </FAQItem>
            ))}
          </FAQList>
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <CTAButton href="/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761">
              Start Your Booking
            </CTAButton>
          </div>
        </Card>

        <Card>
          <SectionTitle>Still Have Questions?</SectionTitle>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#333', margin: 0 }}>
            If you can't find the answer you're looking for, please don't hesitate to get in touch. 
            Visit our <a href="/your-visit" style={{ color: '#c9985e', textDecoration: 'none', fontWeight: 600 }}>Your Visit</a> page for contact information, 
            or explore our <a href="/christmas/your-festive-visit" style={{ color: '#c9985e', textDecoration: 'none', fontWeight: 600 }}>Your Christmas Visit</a> page 
            for more details about building your perfect Christmas package.
          </p>
        </Card>
      </Content>

      <ImageSlant 
        image={festive}
        title="Ready to Book?"
        description="Start building your perfect Christmas package and create magical memories at Belvoir Castle."
        buttonText="Start Your Booking"
        buttonAction={() => window.location.assign('/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761')}
        imagePosition="left"
        darkMode={isBlackFriday}
      />

      <div style={{ margin: '3rem 0' }}>
        <NewsletterSignUp tag="Christmas 2026" />
      </div>
      <Footer />
    </Page>
  );
};

export default YourFestiveVisitFAQs;

